'use client';

import type { RealtyAttributeValue } from '@eliadmin/core/src/types/realty';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useId, useState } from 'react';

import styles from './realty-detail-info.module.css';

export const MOBILE_AMENITIES_LIMIT = 4;

const AmenityArrow = () => (
  <svg aria-hidden="true" className={styles.amenityIcon} focusable="false" viewBox="0 0 24 24">
    <path
      d="M12.127 7.94825H0.75C0.537167 7.94825 0.359 7.87642 0.2155 7.73275C0.0718332 7.58925 0 7.41108 0 7.19825C0 6.98542 0.0718332 6.80725 0.2155 6.66375C0.359 6.52008 0.537167 6.44825 0.75 6.44825H12.127L6.95775 1.279C6.80908 1.13033 6.73567 0.956332 6.7375 0.756999C6.7395 0.557665 6.818 0.380416 6.973 0.22525C7.12817 0.0804164 7.30383 0.00541641 7.5 0.00024974C7.69617 -0.00491693 7.87183 0.0700831 8.027 0.22525L14.3673 6.5655C14.4609 6.65917 14.5269 6.75792 14.5652 6.86175C14.6037 6.96558 14.623 7.07775 14.623 7.19825C14.623 7.31875 14.6037 7.43092 14.5652 7.53475C14.5269 7.63858 14.4609 7.73733 14.3673 7.831L8.027 14.1712C7.8885 14.3097 7.717 14.3806 7.5125 14.3837C7.308 14.3869 7.12817 14.3161 6.973 14.1712C6.818 14.0161 6.7405 13.8379 6.7405 13.6367C6.7405 13.4354 6.818 13.2572 6.973 13.102L12.127 7.94825Z"
      fill="currentColor"
      transform="translate(4.5 4.8)"
    />
  </svg>
);

type RealtyDetailAmenitiesProps = {
  label: string;
  amenities: RealtyAttributeValue[];
  expandLabel: string;
  collapseLabel: string;
};

export const RealtyDetailAmenities = ({
  label,
  amenities,
  expandLabel,
  collapseLabel,
}: RealtyDetailAmenitiesProps) => {
  const [showAll, setShowAll] = useState(false);
  const gridId = useId();
  const hiddenCount = Math.max(0, amenities.length - MOBILE_AMENITIES_LIMIT);
  const toggleText = showAll ? collapseLabel : `${expandLabel} (+${hiddenCount})`;

  return (
    <section className={styles.amenities} data-eli-part="amenities">
      <h2 className={styles.amenitiesTitle}>{label}</h2>
      <ul id={gridId} className={styles.amenitiesGrid} data-eli-part="amenities-grid">
        {amenities.map((amenity, index) => {
          const isSecondary = index >= MOBILE_AMENITIES_LIMIT;

          return (
            <li
              className={cn(styles.amenity, isSecondary && !showAll && styles.amenityMobileHidden)}
              key={amenity.id}
              data-amenity-id={amenity.id}
              data-amenity-priority={isSecondary ? 'secondary' : 'primary'}
            >
              <AmenityArrow />
              <span>{amenity.name}</span>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          className={styles.amenitiesToggle}
          aria-controls={gridId}
          aria-expanded={showAll}
          aria-label={`${toggleText} – ${label}`}
          data-eli-part="amenities-toggle"
          onClick={() => setShowAll(value => !value)}
        >
          {toggleText}
        </button>
      )}
    </section>
  );
};
